import React, { useCallback, useEffect, useState } from "react";
import chatIcon from "../assets/message-chat-circle.png";
import avatar from "../assets/user.png";
import styles from "./styles.module.scss";

/** Let the hero render before the greeting shows up. */
const BUBBLE_DELAY = 1400;
/** How long the "assistant is typing" dots stay before the message. */
const TYPING_TIME = 1000;
/** The greeting has no close button, so it leaves on its own. */
const MESSAGE_TIME = 7000;
const LEAVE_TIME = 400;

type Stage = "hidden" | "typing" | "message" | "leaving";

const SupportLauncher = () => {
  const [stage, setStage] = useState<Stage>("hidden");
  const [dismissed, setDismissed] = useState(false);

  // loader.js draws its own launcher; on this page we show ours instead and reuse
  // the hidden one as the trigger, so the chat itself stays fully the widget's.
  useEffect(() => {
    document.body.classList.add("has-support-launcher");
    return () => document.body.classList.remove("has-support-launcher");
  }, []);

  useEffect(() => {
    if (dismissed) return undefined;

    const timers = [
      window.setTimeout(() => setStage("typing"), BUBBLE_DELAY),
      window.setTimeout(() => setStage("message"), BUBBLE_DELAY + TYPING_TIME),
      window.setTimeout(
        () => setStage("leaving"),
        BUBBLE_DELAY + TYPING_TIME + MESSAGE_TIME
      ),
      window.setTimeout(() => {
        setStage("hidden");
        setDismissed(true);
      }, BUBBLE_DELAY + TYPING_TIME + MESSAGE_TIME + LEAVE_TIME),
    ];

    return () => timers.forEach(window.clearTimeout);
  }, [dismissed]);

  const hideBubble = useCallback(() => {
    setDismissed(true);
    setStage("hidden");
  }, []);

  const openChat = useCallback(() => {
    hideBubble();

    const fab = document.querySelector<HTMLButtonElement>(".sw-fab");
    if (fab) {
      fab.click();
      return;
    }

    // Loader blocked or still loading — the widget also works as a plain page.
    window.open("https://help.macryx.app/", "_blank", "noopener");
  }, [hideBubble]);

  return (
    <div className={styles.launcher}>
      {stage !== "hidden" && (
        <div
          className={`${styles.bubble} ${stage === "leaving" ? styles.leaving : ""}`}
        >
          <img src={avatar} alt="" className={styles.avatar} />

          {stage === "typing" ? (
            <span className={styles.typing} aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          ) : (
            <button type="button" className={styles.bubbleText} onClick={openChat}>
              Hi! How can I help you?
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className={styles.fab}
        aria-label="Open support chat"
        onClick={openChat}
      >
        <img src={chatIcon} alt="" />
      </button>
    </div>
  );
};

export default SupportLauncher;
